/**
 * Chicken — a pot that climbs, a hidden bust point, and one button.
 *
 * **REGISTERED.** It was deliberately absent from `MINI_GAMES` while it was being built, because
 * a client cannot draw a game its own build has never heard of and the client that draws a
 * climbing pot had to be able to ship *before* the server could hand one out. It is in the
 * registry now, which is what lets a contact duel or a duel gun land on it — and what costs every
 * room one draw of its seeded stream per duel. See `MINI_GAMES` for that trade, and for the
 * deploy ordering it still implies.
 *
 * The game, in one paragraph. Two frozen players watch a percentage climb — 1% every
 * CHICKEN_TICKS_PER_PERCENT ticks, so 100% is DUEL_DECIDE_TICKS exactly. Either of them may GRAB
 * at any tick and the first grab takes the pot. Somewhere between CHICKEN_BUST_MIN_PERCENT and
 * CHICKEN_BUST_MAX_PERCENT the pot bursts, at a point drawn per duel and known to neither of
 * them; if it bursts with nobody holding it, both players forfeit it and it lands on the floor as
 * prize shards for the room to race for. Waiting is worth more and worth less every tick, which
 * is the whole of it.
 *
 * **AND GRABBING TOO SOON LOSES.** A second hidden point — the ARM, drawn per duel in
 * [CHICKEN_ARM_MIN_PERCENT, CHICKEN_ARM_MAX_PERCENT) — is what stops the first half of a round
 * being free: a grab strictly before it takes nothing and pays CHICKEN_EARLY_GRAB_PENALTY of the
 * duel's stake to the other player. So there are now two ways to lose that read identically as an
 * outcome and mean opposite things, which is why a round answers a *reason* as well as a verdict.
 * The two bands meet at exactly one point and leave no safe corridor between them; see
 * `chickenPotShare` for the expected-value table that is the actual game.
 *
 * **A GRAB IS PUBLIC THE INSTANT IT HAPPENS**, which is `secretUntilLocked: false` and the exact
 * opposite of rock-paper-scissors' correctness requirement. There it is a rule that a client
 * which could read its opponent's pick would win every duel; here watching the other player
 * flinch *is* the game, and hiding a grab would hide the only thing there is to react to. That is
 * a property of the game rather than of the transport, which is why the declaration lives in this
 * file and `toDuelSnapshot` merely reads it.
 *
 * **THE ANSWER IS A TICK, NOT A PICK**, so this is the first entry in the registry that declares
 * `resolve`: `compare` takes two strings and both of ours can only ever be 'grab', so it cannot
 * see the one thing that decides the round. Everything below is pure — no clock, no PRNG, no
 * state — and the bust point is not here at all: it is drawn once per duel from the room's shared
 * generator and kept on the duel, because it is the one value in this mechanic that must never
 * leave the process.
 */

import {
  CHICKEN_ARM_MAX_PERCENT,
  CHICKEN_ARM_MIN_PERCENT,
  CHICKEN_BONUS_HIGH,
  CHICKEN_BONUS_LOW,
  CHICKEN_BONUS_MID,
  CHICKEN_BONUS_RUNG_HIGH,
  CHICKEN_BONUS_RUNG_LOW,
  CHICKEN_BONUS_RUNG_MID,
  CHICKEN_BUST_MAX_PERCENT,
  CHICKEN_BUST_MIN_PERCENT,
  CHICKEN_EARLY_GRAB_PENALTY,
  CHICKEN_TICKS_PER_PERCENT,
} from '../constants/simulation.js';
import type {
  MiniGame,
  MiniGameChoiceArt,
  MiniGamePot,
  MiniGameRound,
  MiniGameRoundResult,
  MiniGameVerdict,
} from './minigame.js';
import { MINI_GAME_POT_MAX_PERCENT } from './minigame.js';

/** The one thing either player may ever say. `id` is what rides the ordinary input frame. */
export const CHICKEN_GRAB = 'grab';

/**
 * Take it: an arrow dropping into an open basket, in the same 32-unit box the rps drawings use.
 *
 * An object rather than a hand, for rps's stated reason — a hand at 32 px is a blob, and a blob
 * is the one thing this game already has plenty of.
 */
const GRAB_ART: MiniGameChoiceArt = {
  viewBox: '0 0 32 32',
  strokeWidth: 2,
  paths: [
    // The drop: a shaft and a head, so the direction reads before the shape does.
    'M16 4 V15',
    'M11 10.5 L16 16 L21 10.5',
    // The basket it lands in: an open box with a rim, never a closed one — the pot is on the
    // floor and anybody may take it.
    'M6 19 V26 H26 V19',
    'M4 19 H28',
  ],
};

/**
 * The bonus multiplying the pot once the climb is past each rung.
 *
 * Written as a descending walk rather than as a table lookup so the rungs read in the order they
 * are actually reached going backwards, and so an argument outside [0, 100] cannot fall off the
 * end of anything. Below the low rung there is no bonus at all: the pot is worth its own
 * percentage, which is what makes the first forty percent of a round nearly worthless to grab.
 */
export function chickenBonus(percent: number): number {
  if (percent >= CHICKEN_BONUS_RUNG_HIGH) return CHICKEN_BONUS_HIGH;
  if (percent >= CHICKEN_BONUS_RUNG_MID) return CHICKEN_BONUS_MID;
  if (percent >= CHICKEN_BONUS_RUNG_LOW) return CHICKEN_BONUS_LOW;
  return 1;
}

/**
 * THE CURVE: what the pot is worth at `percent`, as a multiple of the duel's ordinary stake.
 *
 * `percent / 100` climbing linearly, times `chickenBonus`. So it runs 0.01x at 1%, 0.39x just
 * under the first rung and 0.60x on it, 0.885x just under the second and 1.35x on it, 1.78x just
 * under the third and 2.80x on it, and 3.50x at a full pot.
 *
 * **WHY A CURVE AT ALL, AND WHY THESE RUNGS.** Two hidden points bracket a round, each drawn
 * uniformly per duel and neither of them on any frame. The ARM is uniform in
 * [CHICKEN_ARM_MIN_PERCENT, CHICKEN_ARM_MAX_PERCENT), so the chance of a grab at `p` being
 * **early** — the grabber loses and pays CHICKEN_EARLY_GRAB_PENALTY to the other player — is
 * `(50 - p) / 50` below 50% and nothing above it. The BUST is uniform in
 * [CHICKEN_BUST_MIN_PERCENT, CHICKEN_BUST_MAX_PERCENT), so the chance of surviving to `p` is 1
 * below 50% and `(100 - p) / 50` above it. The two bands meet at exactly one point and never
 * overlap, so a grab is exposed to exactly one of the two risks and never to neither:
 *
 *     grab at:  10     20     29     40     50     60     70     80     90
 *     EV:      -0.30  -0.16   0.00  +0.40  +0.75  +1.08  +0.95  +1.12  +0.63
 *
 * which is the game stated as a table, and it is pinned column by column in `minigames.test.ts`
 * because it IS the game — a change to any constant above that quietly flattens it is the
 * regression worth catching. Read it in three parts:
 *
 *   - **Below about 29% grabbing is a losing move.** That is what the arm buys, and it is the
 *     half the game was missing: with no penalty every point under the bust floor was risk-free,
 *     so an impatient player paid nothing for being impatient except the pot they walked away
 *     from. Now the first quarter of a round costs money.
 *   - **The crossing is at 29 rather than on a rung**, so 40% is the first place holding is
 *     properly worth doing rather than the first place it stops being a mistake.
 *   - **Above the floor it is the rungs and the bust**, unchanged: four local peaks with the value
 *     sagging *between* them, so the only question worth asking is "do I hold for the next rung"
 *     and holding one rung too long is worse than not holding at all. The best place on the whole
 *     curve is still 80%, where one in five duels bursts before you get there.
 *
 * There is deliberately **no safe corridor anywhere on the bar**: at every percentage a player is
 * trading penalty risk against bust risk, and the 50% they meet at is a one-point knife edge
 * rather than a stretch. A **flat** pot with no penalty under it would make the column fall
 * monotonically from 1%, so grabbing immediately would be strictly correct and the mechanic would
 * be a race to press a button.
 *
 * The reward therefore climbs faster than the risk at exactly three places, which is the card's
 * requirement and is what makes waiting *ever* worth it without making it always worth it.
 *
 * None of this counts the other player, who is the real clock: the first grab takes the pot, so
 * every rung you hold for is a rung they may take it on.
 *
 * Total over the reals, clamped at both ends: it is called at resolution and a throw there would
 * take the room's tick down with it.
 */
export function chickenPotShare(percent: number): number {
  const climb =
    percent < 0 ? 0 : percent > MINI_GAME_POT_MAX_PERCENT ? MINI_GAME_POT_MAX_PERCENT : percent;
  return (climb / MINI_GAME_POT_MAX_PERCENT) * chickenBonus(climb);
}

/** The pot the duel phase reads. Every number in it is derived; none is chosen here. */
export const CHICKEN_POT: MiniGamePot = {
  ticksPerPercent: CHICKEN_TICKS_PER_PERCENT,
  bustMinPercent: CHICKEN_BUST_MIN_PERCENT,
  bustMaxPercent: CHICKEN_BUST_MAX_PERCENT,
  // The penalty band, 0..50 — declared, so an overlay may shade it and a bot may reason about it,
  // while the point *this* duel arms on stays on `MiniGameRoundSecrets` and off every frame. Its
  // ceiling is CHICKEN_BUST_MIN_PERCENT itself, which is what makes the two bands meet at one
  // point rather than leave a corridor between them.
  armMinPercent: CHICKEN_ARM_MIN_PERCENT,
  armMaxPercent: CHICKEN_ARM_MAX_PERCENT,
  earlyGrabPenalty: CHICKEN_EARLY_GRAB_PENALTY,
  share: chickenPotShare,
};

/**
 * Decide a round: **the FIRST grab wins unless it was too early, and two grabs on the same tick
 * are a draw.**
 *
 * The tie rule is the one place this game had a genuinely free choice, and a draw is the only
 * answer that needs no tiebreak. Every alternative has to name a winner from something that is
 * not the game — the lower player id, the `a`/`b` ordering, whoever the collision sweep happened
 * to walk first — and the last of those is not even deterministic across two hosts. A draw is
 * also the *same* outcome as nobody grabbing, which means it goes down the one path this game
 * already has for "nobody won": the pot bursts onto the floor as prize shards, both players pay
 * it, and the round stays exactly zero-sum. Two players who flinched together lose it to the
 * room, which is the right story as well as the cheap rule.
 *
 * **A TIE STAYS A DRAW EVEN UNDER THE ARM**, and it is not an oversight: an early grab pays *the
 * other player*, and two players who both grabbed early have no other player between them to owe.
 * There is nobody to pay, so there is nothing to do but what a draw already does.
 *
 * **THE ARM POINT IS READ OFF `round.secrets` AND NOTHING ELSE.** It is drawn per duel from the
 * room's shared stream and handed in on the round, which is what keeps the rule in this file: the
 * duel phase asks the running game to decide and never asks whether the running game is chicken.
 * A round with no secrets at all — or a duel with no arm point — has nothing to be early of, so
 * an absent one reads as "no penalty band", which is the safe way round.
 *
 * A side that never grabbed loses to one that did, and neither grabbing is a draw. Nothing here
 * reads `choice`: both sides can only ever answer 'grab', so the *when* is the whole answer, and
 * a chicken duel is never answered for at a timeout — see `autoChoice`.
 */
export function resolveChickenRound(round: MiniGameRound): MiniGameRoundResult {
  const a = round.a.lockedAtTick;
  const b = round.b.lockedAtTick;
  // Nobody grabbed, or both grabbed on the same tick. Either way the pot goes to the floor, and
  // the two arrive here as one outcome because they are one outcome.
  if (a === null && b === null) return { verdict: 0, reason: 'burst' };
  if (a !== null && b !== null && a === b) return { verdict: 0, reason: 'burst' };

  // Exactly one earliest grab from here. In the running simulation it is also the *only* grab —
  // the room reveals the duel on the tick it sees one and `lockDuelChoice` refuses everything
  // afterwards — so the comparison below is the belt rather than the braces.
  const firstIsA = b === null || (a !== null && a < b);
  const grabbedAt = (firstIsA ? a : b) as number;

  const arm = round.secrets?.armAtTick ?? null;
  if (arm !== null && grabbedAt < arm) {
    // Early: the grabber pays. The verdict names the *other* side as the winner, which is what
    // carries the penalty in the right direction through the duel's ordinary payout path — there
    // is no separate "who pays" channel and there must not be one.
    return { verdict: firstIsA ? -1 : 1, reason: 'early_grab' };
  }
  return { verdict: firstIsA ? 1 : -1, reason: 'grabbed_first' };
}

export const chicken: MiniGame = {
  id: 'chicken',
  name: 'Chicken',
  /**
   * A grab is public the instant it happens. See the note at the top of this file: this is the
   * declaration `miniGameKeepsChoicesSecret` was written for, and the only way to get it.
   */
  secretUntilLocked: false,
  /**
   * What the instructions page says about this game, in this game's own file.
   *
   * It names the two bands as *halves* rather than as numbers, deliberately: the numbers are on
   * the pot bar the page mounts beside this prose (`DuelPotBar`, driven by the same declared
   * ranges), so restating them here would be two places to change and one of them would be
   * missed. What only words can say is that both points are drawn per duel and told to nobody —
   * which is the whole game and is on no frame anywhere.
   */
  howToPlay: {
    summary:
      'One button, and the only question is when. The pot climbs while you both watch it, and the first grab takes it.',
    rules: [
      'Somewhere in the second half it bursts, at a point drawn for this duel that neither of you is told.',
      'If it bursts with nobody holding it you both pay, and it lands on the floor as prize shards for the whole room to race for.',
      'Somewhere in the first half it arms — also hidden. Grab before that and you take nothing and pay the other player instead.',
      'The bonus steps up at each rung, so waiting is worth more every tick, right up until it is worth nothing.',
      'A grab is public the instant it happens. Watching the other player flinch is the game.',
      'Both grab on the same tick and nobody wins it: that is a burst too.',
    ],
  },
  choices: [{ id: CHICKEN_GRAB, label: 'Grab', glyph: '✊', art: GRAB_ART }],
  pot: CHICKEN_POT,
  compare(): MiniGameVerdict {
    // Unreachable through the duel, which resolves this game through `resolve` below. Both sides
    // can only ever answer 'grab', so two choices genuinely carry no information — a draw is the
    // honest answer rather than a throw, which is `rockPaperScissors.compare`'s own rule for a
    // pair it cannot score and for its reason.
    return 0;
  },
  resolve: resolveChickenRound,
  autoChoice() {
    /**
     * **NEVER REACHED, AND THAT IS THE POINT.** The duel phase does not answer for a side in a
     * game with a pot: "never grabbed" is a real answer to chicken and an auto-grab would invent
     * one, at a tick nobody chose, in the one game where the tick is the entire answer. The pot
     * also bursts strictly before CHICKEN_BUST_MAX_PERCENT, so the duel's own answering window
     * never closes on a chicken duel to begin with.
     *
     * It is here because the interface requires it, and it returns the only choice there is.
     */
    return CHICKEN_GRAB;
  },
};
